import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useApiKeys } from '../../../hooks/useApiKeys';
import { CreateApiKeyDialog } from '../../../components/api-keys/CreateApiKeyDialog';
import { KeyRevealModal } from '../../../components/api-keys/KeyRevealModal';
import { RotateKeyDialog } from '../../../components/api-keys/RotateKeyDialog';
import { DeleteConfirmationDialog } from '../../../components/shared/DeleteConfirmationDialog';
import { EmptyState } from '../../../components/shared';
import type { ApiKeyMeta, CreateApiKeyInput } from '../../../types/apiKeys';
import { formatDate } from '../../../lib/dateFormat';
import '@tylertech/forge/button';
import '@tylertech/forge/icon';
import './index.css';

export const Route = createFileRoute('/settings/api-keys/')({
  component: ApiKeysPage,
});

export { ApiKeysPage };

const ForgeButton = 'forge-button' as React.ElementType;

function StatusBadge({ status }: { status: ApiKeyMeta['status'] }): JSX.Element {
  const cls = `api-key-status-badge api-key-status-badge--${status.toLowerCase()}`;
  return <span className={cls}>{status}</span>;
}

function ApiKeysPage(): JSX.Element {
  const { keys, loading, error, createKey, rotateKey, revokeKey } = useApiKeys();

  const [createOpen, setCreateOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  const [revealKey, setRevealKey] = useState<string | null>(null);

  const [rotateTarget, setRotateTarget] = useState<ApiKeyMeta | null>(null);
  const [isRotating, setIsRotating] = useState(false);

  const [revokeTarget, setRevokeTarget] = useState<ApiKeyMeta | null>(null);
  const [isRevoking, setIsRevoking] = useState(false);

  const handleCreate = async (input: CreateApiKeyInput): Promise<void> => {
    setIsCreating(true);
    try {
      const response = await createKey(input);
      setCreateOpen(false);
      setRevealKey(response.rawKey);
    } finally {
      setIsCreating(false);
    }
  };

  const handleRotateConfirm = async (): Promise<void> => {
    if (!rotateTarget) return;
    setIsRotating(true);
    try {
      const response = await rotateKey(rotateTarget.keyId);
      setRotateTarget(null);
      setRevealKey(response.rawKey);
    } finally {
      setIsRotating(false);
    }
  };

  const handleRevoke = async (): Promise<void> => {
    if (!revokeTarget) return;
    setIsRevoking(true);
    try {
      await revokeKey(revokeTarget.keyId);
      setRevokeTarget(null);
    } finally {
      setIsRevoking(false);
    }
  };

  if (loading) {
    return (
      <div className="api-keys-page">
        <div className="api-keys-loading" aria-busy="true" aria-label="Loading API keys">
          <div className="api-keys-skeleton"></div>
          <div className="api-keys-skeleton"></div>
          <div className="api-keys-skeleton"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="api-keys-page">
        <p className="api-keys-error" role="alert">
          Failed to load API keys: {error}
        </p>
      </div>
    );
  }

  return (
    <div className="api-keys-page">
      <div className="api-keys-header">
        <h1 className="forge-typography--headline4">API Keys</h1>
        <ForgeButton
          type="button"
          variant="raised"
          onClick={() => setCreateOpen(true)}
          data-testid="new-api-key-button"
        >
          <forge-icon slot="start" name="add" aria-hidden="true"></forge-icon>
          New API Key
        </ForgeButton>
      </div>

      <section className="api-keys-docs-callout" aria-label="API documentation">
        <forge-icon name="menu_book" aria-hidden="true"></forge-icon>
        <div className="api-keys-docs-callout-text">
          <strong>New to the API?</strong> See the{' '}
          <Link to="/settings/api-docs" data-testid="api-docs-link">
            interactive API documentation
          </Link>{' '}
          — Swagger UI, the GraphQL explorer, MCP setup, and which scope each endpoint needs.
        </div>
      </section>

      {keys.length === 0 ? (
        <EmptyState
          icon="vpn_key"
          title="No API keys yet"
          description="Create an API key to allow machine-to-machine access for CI/CD and integrations."
          actionLabel="New API Key"
          onAction={() => setCreateOpen(true)}
        />
      ) : (
        <div className="api-keys-table-wrapper">
          <table className="api-keys-table" role="table" aria-label="API Keys">
            <thead>
              <tr>
                <th>Name</th>
                <th>Scopes</th>
                <th>Status</th>
                <th>Expires</th>
                <th>Last Used</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {keys.map((key) => (
                <tr key={key.keyId} data-testid={`key-row-${key.keyId}`}>
                  <td>{key.keyName}</td>
                  <td>
                    <span className="api-key-scopes">{key.scopes.join(', ')}</span>
                  </td>
                  <td>
                    <StatusBadge status={key.status} />
                  </td>
                  <td>{formatDate(key.expiresAt)}</td>
                  <td>{key.lastUsedAt ? formatDate(key.lastUsedAt) : '—'}</td>
                  <td>
                    <div className="api-key-actions">
                      {key.status === 'Active' && (
                        <>
                          <ForgeButton
                            type="button"
                            variant="text"
                            onClick={() => setRotateTarget(key)}
                            aria-label={`Rotate key ${key.keyName}`}
                          >
                            Rotate
                          </ForgeButton>
                          <ForgeButton
                            type="button"
                            variant="text"
                            className="api-key-revoke-btn"
                            onClick={() => setRevokeTarget(key)}
                            aria-label={`Revoke key ${key.keyName}`}
                          >
                            Revoke
                          </ForgeButton>
                        </>
                      )}
                      {key.status === 'Rotating' && (
                        <ForgeButton
                          type="button"
                          variant="text"
                          className="api-key-revoke-btn"
                          onClick={() => setRevokeTarget(key)}
                          aria-label={`Revoke key ${key.keyName}`}
                        >
                          Revoke
                        </ForgeButton>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CreateApiKeyDialog
        open={createOpen}
        isSubmitting={isCreating}
        onSubmit={(input) => void handleCreate(input)}
        onCancel={() => setCreateOpen(false)}
      />

      <KeyRevealModal
        open={revealKey !== null}
        rawKey={revealKey}
        onDismiss={() => setRevealKey(null)}
      />

      <RotateKeyDialog
        open={rotateTarget !== null}
        keyName={rotateTarget?.keyName ?? ''}
        isRotating={isRotating}
        onConfirm={() => void handleRotateConfirm()}
        onCancel={() => setRotateTarget(null)}
      />

      <DeleteConfirmationDialog
        open={revokeTarget !== null}
        title="Revoke API Key"
        message="Are you sure you want to revoke this API key? It will stop working immediately."
        itemName={revokeTarget?.keyName ?? ''}
        onConfirm={() => void handleRevoke()}
        onCancel={() => setRevokeTarget(null)}
        isDeleting={isRevoking}
      />
    </div>
  );
}
